import { createClient, type SupabaseClient } from '@supabase/supabase-js';

import type { Database } from './database.types.ts';

export type Db = SupabaseClient<Database>;

/**
 * Browser client for the admin SPA.
 *
 * Carries the staff user's JWT, so every request is subject to flat claim-RLS
 * (`account_id = any(app_metadata.account_ids)`). This is the *primary*
 * enforcement point for the admin — there is no server proxy in front of it.
 */
export function createBrowserDb(url: string, anonKey: string): Db {
  return createClient<Database>(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  });
}

/**
 * Service-role client for trusted server paths (the Hono API).
 *
 * WARNING: this bypasses RLS. Never construct it outside a Worker, and never
 * query with it directly — go through the repositories in `./repositories`,
 * which require a `TenantScope`.
 */
export function createServiceDb(url: string, serviceRoleKey: string): Db {
  return createClient<Database>(url, serviceRoleKey, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: { headers: { 'x-ctrl-client': 'service' } },
  });
}

/**
 * Anon client bound to a caller's access token — used when we want a server
 * request to still be subject to RLS (e.g. verifying a staff JWT's reach).
 */
export function createUserDb(url: string, anonKey: string, accessToken: string): Db {
  return createClient<Database>(url, anonKey, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: { headers: { Authorization: `Bearer ${accessToken}` } },
  });
}
